import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Printer, CreditCard, WalletCards, MonitorCog } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { EmptyState } from '@/components/pos/EmptyState';
import { useDeviceBinding } from '@/hooks/queries';
import { useDeviceOptions } from '@/hooks/queries/useDevices';
import { useBindDevice } from '@/hooks/mutations';
import { useDeviceStore } from '@/stores/device.store';
import { useAuthStore } from '@/stores/auth.store';
import { env } from '@/lib/env';
import { useT } from '@/lib/i18n';
import { LanguageSwitch } from '@/components/pos/LanguageSwitch';
import type { ApiError } from '@/types/api';

const NONE = '__none__';

export function DeviceSetupPage() {
  const navigate = useNavigate();
  const { t } = useT();
  const user = useAuthStore((s) => s.user);
  const { data: binding, isLoading: bindingLoading } = useDeviceBinding();
  const { data: printers = [], isLoading: printersLoading } = useDeviceOptions('TICKET_PRINTER');
  const { data: readers = [], isLoading: readersLoading } = useDeviceOptions('CASHLESS_READER');
  const { data: edcTerminals = [], isLoading: edcLoading } = useDeviceOptions('EDC_TERMINAL');
  const { mutate: bind, isPending } = useBindDevice();
  const storeReader = useDeviceStore((s) => s.reader);

  const [name, setName] = useState('');
  const [printerId, setPrinterId] = useState<string>(NONE);
  const [readerId, setReaderId] = useState<string>(NONE);
  const [edcTerminalId, setEdcTerminalId] = useState<string>(NONE);
  const [mid, setMid] = useState(storeReader.mid);
  const [tid, setTid] = useState(storeReader.tid);

  useEffect(() => {
    if (binding) {
      setName(binding.name ?? `Loket ${user?.name ?? ''}`.trim());
      setPrinterId(binding.ticketPrinterId ?? NONE);
      setReaderId(binding.cashlessReaderId ?? NONE);
      setEdcTerminalId(binding.edcTerminalId ?? NONE);
      if (binding.reader) {
        setMid(binding.reader.mid);
        setTid(binding.reader.tid);
      }
    } else if (!name) {
      setName(`Loket ${user?.name ?? user?.email ?? ''}`.trim());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [binding]);

  const save = () => {
    if (printerId === NONE) {
      toast.error(t('device.toast.pickPrinter'));
      return;
    }
    bind(
      {
        name: name.trim() || 'Loket Ticketbox',
        type: 'TICKETBOX',
        ticketPrinterId: printerId === NONE ? null : printerId,
        cashlessReaderId: readerId === NONE ? null : readerId,
        edcTerminalId: edcTerminalId === NONE ? null : edcTerminalId,
      },
      {
        onSuccess: () => {
          // mid/tid are carried locally for the cashless socket reader payload.
          useDeviceStore.setState({ reader: { mid: mid.trim(), tid: tid.trim() } });
          toast.success(t('device.toast.bound'));
          navigate('/', { replace: true });
        },
        onError: (err) => toast.error((err as ApiError)?.message ?? t('device.toast.saveFailed')),
      },
    );
  };

  const loading = bindingLoading || printersLoading || readersLoading || edcLoading;
  const noPrinters = !printersLoading && printers.length === 0;

  return (
    <div className="mx-auto h-full max-w-2xl overflow-y-auto px-6 py-10 pos-scroll">
      <div className="mb-6 flex items-center gap-3">
        <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary text-primary-foreground">
          <MonitorCog className="h-6 w-6" />
        </span>
        <div className="flex-1">
          <h1 className="font-display text-2xl font-bold tracking-tight text-ink-900">{t('device.title')}</h1>
          <p className="text-sm text-muted-foreground">{t('device.subtitle')}</p>
        </div>
        <LanguageSwitch className="self-start" />
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-ink-400">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      ) : noPrinters ? (
        <EmptyState
          icon={Printer}
          title={t('device.noPrinters')}
          description={t('device.noPrintersDesc')}
        />
      ) : (
        <div className="space-y-5">
          <Card>
            <CardHeader>
              <CardTitle>{t('device.identity')}</CardTitle>
              <CardDescription>{t('device.identityDesc')}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-1.5">
                <Label htmlFor="dev-name">{t('device.name')}</Label>
                <Input id="dev-name" value={name} onChange={(e) => setName(e.target.value)} placeholder={t('device.namePlaceholder')} />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Printer className="h-5 w-5 text-primary" /> {t('device.printer')}
                <span className="text-destructive">*</span>
              </CardTitle>
              <CardDescription>{t('device.printerDesc')}</CardDescription>
            </CardHeader>
            <CardContent>
              <Select value={printerId} onValueChange={setPrinterId}>
                <SelectTrigger>
                  <SelectValue placeholder={t('device.printerPick')} />
                </SelectTrigger>
                <SelectContent>
                  {printers.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="h-5 w-5 text-primary" /> {t('device.reader')}
              </CardTitle>
              <CardDescription>{t('device.readerDesc')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Select value={readerId} onValueChange={setReaderId}>
                <SelectTrigger>
                  <SelectValue placeholder={t('device.readerPick')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>{t('device.none')}</SelectItem>
                  {readers.map((r) => (
                    <SelectItem key={r.id} value={r.id}>
                      {r.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="mid">Reader MID</Label>
                  <Input id="mid" value={mid} onChange={(e) => setMid(e.target.value)} className="tnum" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="tid">Reader TID</Label>
                  <Input id="tid" value={tid} onChange={(e) => setTid(e.target.value)} className="tnum" />
                </div>
              </div>
              <p className="text-xs text-ink-400">
                {t('device.readerDefault', { mid: env.CASHLESS_READER_MID || '—', tid: env.CASHLESS_READER_TID || '—' })}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <WalletCards className="h-5 w-5 text-primary" /> {t('device.edc')}
              </CardTitle>
              <CardDescription>{t('device.edcDesc')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <Select value={edcTerminalId} onValueChange={setEdcTerminalId}>
                <SelectTrigger>
                  <SelectValue placeholder={t('device.edcPick')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>{t('device.none')}</SelectItem>
                  {edcTerminals.map((tItem) => (
                    <SelectItem key={tItem.id} value={tItem.id}>
                      {tItem.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {/* No MID/TID inputs here, unlike the e-money reader above. Those
                  are carried locally for the cashless socket payload; a card
                  terminal is provisioned with its MID/TID by the bank and no
                  EDC frame carries them. They are recorded (optionally) on the
                  Device row in the back office and checked at settlement, so
                  asking the cashier for them would only invite a mismatch. */}
              <p className="text-xs text-ink-400">
                {t('device.edcNote')}
              </p>
            </CardContent>
          </Card>

          <Button size="lg" className="h-14 w-full text-lg" onClick={save} disabled={isPending}>
            {isPending ? <Loader2 className="h-5 w-5 animate-spin" /> : t('device.save')}
          </Button>
        </div>
      )}
    </div>
  );
}
